import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Diálogo modal sobre o <dialog> nativo.
 *
 * O que o navegador entrega de graça e a implementação anterior (uma div com
 * z-index) não tinha: foco preso dentro do diálogo, Esc para fechar, `inert`
 * no conteúdo de trás, camada superior sem disputa de z-index, e devolução do
 * foco ao elemento que abriu.
 *
 * O que ainda é responsabilidade nossa: o nome acessível. Um diálogo sem
 * `aria-labelledby` é anunciado como "diálogo" e nada mais — era o caso dos
 * 11 diálogos do produto anterior.
 */
export function Dialog({
  open, onClose, title, description, children, footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = `dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // showModal() em diálogo já aberto lança InvalidStateError.
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      /* `cancel` é o Esc. Sem interceptar, o <dialog> fecha sozinho e o estado
         do React continua achando que está aberto. */
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={onClose}
    >
      <div className="dialog__head">
        <div>
          <h2 className="dialog__title" id={titleId}>{title}</h2>
          {description && <p className="card__sub">{description}</p>}
        </div>
        {/* Ícone, não o caractere "✕": ele herda a métrica da fonte, fica
            fora do centro óptico do botão e alguns leitores de tela o anunciam
            como "multiplicação". */}
        <button type="button" className="drawer__fechar" onClick={onClose} aria-label={`Fechar ${title}`}>
          <X size={20} aria-hidden="true" />
        </button>
      </div>
      <div className="dialog__body">{children}</div>
      {footer && <div className="dialog__foot">{footer}</div>}
    </dialog>
  );
}
